/**
 * AccountsConsole — staff account-admin tools (ownership, refresh, counters,
 * new-hire setup). Most are keyed by Stripe customer ID; a couple by email.
 *
 * Frontend only for now: submit is stubbed pending the staff-api mutations
 * (each card's exact Mongo write is being finalized). Built with a generic
 * multi-field card so each tool just declares its fields.
 */
import { useState, type ReactNode } from 'react';
import { Button, Input, Label } from '@staff-app/ui';
import {
  useForceRefresh,
  useChangeCompanyOwner,
  useChangeTeamOwner,
  useResetEngageUsage,
  useResetCardCounter,
  useNewHireEnableAll,
} from '../hooks/useAccountsConsole';
import { MoveContentConsole } from './MoveContentConsole';
import { MigrateBrandingConsole } from './MigrateBrandingConsole';

/** Run the action for a card; returns a success message (throws on error). */
type SubmitFn = (values: Record<string, string>) => Promise<string>;

interface FieldDef {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'date';
  placeholder?: string;
  helper?: string;
  /** Must start with "cus_" to be valid. */
  stripe?: boolean;
}

interface AccountActionConfig {
  key: string;
  title: string;
  description: string;
  fields: FieldDef[];
  buttonLabel: string;
}

const STRIPE_HELP = 'Enter the Stripe Customer ID (e.g., cus_ABC123)';

const ACTIONS: AccountActionConfig[] = [
  {
    key: 'force-refresh',
    title: 'Force Refresh Company Account',
    description:
      "Reactivates a customer's company account (payment.isActive) and pushes the change to their screens, so they refresh without waiting for the 12-hour device cache to expire. Use after reactivating a deactivated account.",
    fields: [
      { name: 'stripeId', label: 'Stripe Customer ID', placeholder: 'cus_...', helper: STRIPE_HELP, stripe: true },
    ],
    buttonLabel: 'Force Refresh Account',
  },
  {
    key: 'change-company-owner',
    title: 'Change Company Owner',
    description: 'Update company ownership by demoting the current owner and promoting a new owner.',
    fields: [
      { name: 'currentOwner', label: 'Current Owner Email', type: 'email', placeholder: 'current@example.com' },
      { name: 'newOwner', label: 'New Owner Email', type: 'email', placeholder: 'newowner@example.com' },
    ],
    buttonLabel: 'Change Owner',
  },
  {
    key: 'change-team-owner',
    title: 'Change Team Owner',
    description: 'Update team ownership by demoting the current team owner and promoting a new team owner.',
    fields: [
      { name: 'currentTeamOwner', label: 'Current Team Owner Email', type: 'email', placeholder: 'currentteamowner@example.com' },
      { name: 'newTeamOwner', label: 'New Team Owner Email', type: 'email', placeholder: 'newteamowner@example.com' },
    ],
    buttonLabel: 'Change Team Owner',
  },
  {
    key: 'reset-engage-usage',
    title: 'Reset Free Engage Usage',
    description: "Reset the customer's free Engage screen usage counter back to 0.",
    fields: [
      { name: 'stripeId', label: 'Stripe Customer ID', placeholder: 'cus_...', helper: STRIPE_HELP, stripe: true },
    ],
    buttonLabel: 'Reset Engage Usage',
  },
  {
    key: 'reset-card-counter',
    title: 'Reset Credit Card Update Counter',
    description: "Reset the customer's credit card input counter during payment process (cardSetting.countInputed).",
    fields: [
      { name: 'stripeId', label: 'Stripe Customer ID', placeholder: 'cus_...', helper: STRIPE_HELP, stripe: true },
    ],
    buttonLabel: 'Reset Card Counter',
  },
  {
    key: 'new-hire',
    title: 'New Hire - Enable All Features',
    description: 'Enable all features for a new hire account with Engage plan trial (10 screens, 10GB storage).',
    fields: [
      { name: 'stripeId', label: 'Stripe Customer ID', placeholder: 'cus_...', helper: STRIPE_HELP, stripe: true },
      { name: 'trialEndDate', label: 'Trial End Date', type: 'date' },
    ],
    buttonLabel: 'Enable All Features',
  },
];

// Strict email check, matching the ops-runbook validator.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TODAY = new Date().toISOString().slice(0, 10);

function fieldValid(field: FieldDef, raw: string): boolean {
  const v = raw.trim();
  if (!v) return false;
  if (field.stripe && !v.startsWith('cus_')) return false;
  if (field.type === 'email' && !EMAIL_RE.test(v)) return false;
  return true;
}

function AccountActionCard({
  config,
  onSubmit,
}: {
  config: AccountActionConfig;
  onSubmit?: SubmitFn;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const valid = config.fields.every((f) => fieldValid(f, values[f.name] ?? ''));

  const handleSubmit = async () => {
    setBusy(true);
    setMessage(null);
    try {
      if (!onSubmit) {
        setMessage({ kind: 'ok', text: 'Pending backend wiring.' });
        return;
      }
      const text = await onSubmit(values);
      setMessage({ kind: 'ok', text });
    } catch (e: unknown) {
      setMessage({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid={`accounts-card-${config.key}`}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{config.title}</h3>
        <p className="text-xs text-muted-foreground">{config.description}</p>
      </div>

      {config.fields.map((field) => (
        <div key={field.name} className="flex flex-col gap-1.5">
          <Label htmlFor={`${config.key}-${field.name}`} className="text-xs">
            {field.label}
          </Label>
          <Input
            id={`${config.key}-${field.name}`}
            type={field.type === 'date' ? 'date' : 'text'}
            value={values[field.name] ?? ''}
            onChange={(e) => setValues((s) => ({ ...s, [field.name]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valid && !busy) handleSubmit();
            }}
            placeholder={field.placeholder}
            min={field.type === 'date' ? TODAY : undefined}
            data-testid={`accounts-${config.key}-${field.name}`}
          />
          {field.helper ? <p className="text-[11px] text-muted-foreground">{field.helper}</p> : null}
        </div>
      ))}

      <Button
        onClick={handleSubmit}
        disabled={!valid || busy}
        className="w-fit cursor-pointer"
        data-testid={`accounts-${config.key}-submit`}
      >
        {busy ? 'Working...' : config.buttonLabel}
      </Button>

      {message ? (
        <p
          className={message.kind === 'ok' ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}
          data-testid={`accounts-${config.key}-result`}
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

export function AccountsConsole() {
  const [forceRefresh] = useForceRefresh();
  const [changeCompanyOwner] = useChangeCompanyOwner();
  const [changeTeamOwner] = useChangeTeamOwner();
  const [resetEngageUsage] = useResetEngageUsage();
  const [resetCardCounter] = useResetCardCounter();
  const [newHireEnableAll] = useNewHireEnableAll();

  const withCompany = (r: { companyName?: string | null; message: string }) =>
    r.companyName ? `${r.companyName} — ${r.message}` : r.message;

  // Wired actions return a success message; the rest fall back to the stub.
  const submitByKey: Record<string, SubmitFn> = {
    'force-refresh': async (v) =>
      withCompany(
        (await forceRefresh({ variables: { stripeId: v.stripeId } })).data!.staffAccountsForceRefresh,
      ),
    'reset-engage-usage': async (v) =>
      withCompany(
        (await resetEngageUsage({ variables: { stripeId: v.stripeId } })).data!
          .staffAccountsResetEngageUsage,
      ),
    'reset-card-counter': async (v) =>
      withCompany(
        (await resetCardCounter({ variables: { stripeId: v.stripeId } })).data!
          .staffAccountsResetCardCounter,
      ),
    'new-hire': async (v) =>
      withCompany(
        (await newHireEnableAll({ variables: { stripeId: v.stripeId, trialEndDate: v.trialEndDate } }))
          .data!.staffAccountsNewHireEnableAll,
      ),
    'change-company-owner': async (v) =>
      withCompany(
        (
          await changeCompanyOwner({
            variables: { currentOwnerEmail: v.currentOwner, newOwnerEmail: v.newOwner },
          })
        ).data!.staffAccountsChangeCompanyOwner,
      ),
    'change-team-owner': async (v) =>
      withCompany(
        (
          await changeTeamOwner({
            variables: { currentOwnerEmail: v.currentTeamOwner, newOwnerEmail: v.newTeamOwner },
          })
        ).data!.staffAccountsChangeTeamOwner,
      ),
  };

  // Cards render in one ordered sequence so bespoke cards (which the generic
  // AccountActionCard can't express — multi-step flows) can sit ANYWHERE in the
  // grid rather than only being appended last. `after` places a custom card
  // immediately following that generic card's key.
  const CUSTOM_AFTER: Record<string, { key: string; node: ReactNode }> = {
    // Migrate Branding sits immediately right of Force Refresh (grid index 1).
    'force-refresh': { key: 'migrate-branding', node: <MigrateBrandingConsole /> },
  };

  const cards: ReactNode[] = [];
  for (const action of ACTIONS) {
    cards.push(
      <AccountActionCard key={action.key} config={action} onSubmit={submitByKey[action.key]} />,
    );
    const custom = CUSTOM_AFTER[action.key];
    if (custom) cards.push(<div key={custom.key} className="contents">{custom.node}</div>);
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="accounts-console">
      {cards}
      <MoveContentConsole />
    </div>
  );
}
